import { useState, useEffect } from 'react';
import { Form } from 'antd';
import { useIntl } from 'react-intl';
import useQueryApiClient from '../../../../../utils/useQueryApiClient';
import { Product } from '../../../../../interfaces/BackendModels';
import { Button } from 'ui';
import { Select, SelectOption } from 'ui/Select';
import { InputNumber } from 'ui/InputNumber';
import { StyledAddProductModal } from './AddProductModalStyle';
import { getUnitShort } from '../../../../../utils/unitMapping';

interface AddProductModalProps {
  open: boolean;
  onClose: () => void;
  onAdd: (productData: {
    product_id: number;
    product_name: string;
    stock_price: number;
    stock_count: number;
    stock_unit: string;
    stock_total: number;
    product_category_id: number;
    product_subcategory_id: number;
    vat: number;
  }) => void;
  farmId?: number;
  editItem?: {
    product_id: number;
    product_name: string;
    stock_price: string | number;
    stock_count: string | number;
    stock_unit: string;
    product_category_id?: number;
    product_subcategory_id?: number;
  };
  editIndex?: number;
}

interface ProductResponse {
  products: {
    data: Product[];
  };
}

export const AddProductModal = ({
  open,
  onClose,
  onAdd,
  farmId,
  editItem,
  editIndex,
}: AddProductModalProps) => {
  const intl = useIntl();
  const [form] = Form.useForm();
  const [selectedProductId, setSelectedProductId] = useState<number | null>(null);
  const isEditMode = editItem !== undefined;

  const { data, isLoading, appendData } = useQueryApiClient({
    request: {
      url: 'api/v2/products',
      disableOnMount: true,
    },
  });

  useEffect(() => {
    if (farmId && open) {
      appendData({
        page: 1,
        page_size: 1000,
        filter: { farm_id: farmId }
      });
    }
  }, [farmId, open]);

  const products = (data as ProductResponse)?.products?.data ?? [];

  const selectedProduct = products.find((p: Product) => p.id === selectedProductId);

  interface ProductAmount {
    type: string;
    storage_unit?: string;
    storage_wholesale_price?: number;
  }

  const productAmountsArray: ProductAmount[] = (selectedProduct as Product & { product_amounts?: ProductAmount[]; productAmounts?: ProductAmount[] })?.product_amounts ||
    (selectedProduct as Product & { product_amounts?: ProductAmount[]; productAmounts?: ProductAmount[] })?.productAmounts || [];
  const productAmounts = productAmountsArray.find(
    (pa: ProductAmount) => pa.type === 'WHOLESALE'
  );

  const handleAdd = async () => {
    try {
      const values = await form.validateFields();

      if (!selectedProduct) {
        return;
      }

      const stockUnit = isEditMode
        ? editItem!.stock_unit
        : productAmounts?.storage_unit || 'kg';

      const productData = {
        product_id: selectedProduct.id,
        product_name: selectedProduct.name,
        stock_price: values.stock_price,
        stock_count: values.stock_count,
        stock_unit: stockUnit,
        stock_total: values.stock_price * values.stock_count,
        product_category_id: isEditMode && editItem?.product_category_id
          ? editItem.product_category_id
          : selectedProduct.category_id,
        product_subcategory_id: isEditMode && editItem?.product_subcategory_id
          ? editItem.product_subcategory_id
          : selectedProduct.product_id,
        vat: 21,
      };

      onAdd(productData);
      handleClose();
    } catch (error) {
      console.error('Validation failed:', error);
    }
  };

  const handleClose = () => {
    form.resetFields();
    setSelectedProductId(null);
    onClose();
  };

  useEffect(() => {
    if (editItem && open && products.length > 0) {
      setSelectedProductId(editItem.product_id);
      form.setFieldsValue({
        product_id: editItem.product_id,
        stock_price: Number(editItem.stock_price),
        stock_count: Number(editItem.stock_count),
      });
    }
  }, [editItem, open, products, form]);

  useEffect(() => {
    if (selectedProduct && !isEditMode) {
      if (productAmounts) {
        form.setFieldsValue({
          stock_price: productAmounts.storage_wholesale_price || 0,
          stock_count: 1,
        });
      } else {
        form.setFieldsValue({
          stock_price: 0,
          stock_count: 1,
        });
      }
    }
  }, [selectedProductId, selectedProduct, productAmounts, form, isEditMode]);

  return (
    <StyledAddProductModal
      open={open}
      onCancel={handleClose}
      destroyOnClose
      zIndex={1200}
      title={intl.formatMessage({ id: isEditMode ? 'general.edit_product' : 'general.add_product' })}
      footer={[
        <Button key="cancel" onClick={handleClose} label={intl.formatMessage({ id: 'general.cancel' })} />,
        <Button
          key="add"
          type="primary"
          onClick={handleAdd}
          disabled={!selectedProductId}
          label={intl.formatMessage({ id: isEditMode ? 'general.save' : 'general.add' })}
        />,
      ]}
    >
      <Form form={form} layout="vertical">
        <Select
          name="product_id"
          label={intl.formatMessage({ id: 'general.product' })}
          rules={[
            { required: true, message: intl.formatMessage({ id: 'general.required' }) },
          ]}
          showSearch
          placeholder={intl.formatMessage({ id: 'general.select_product' })}
          loading={isLoading}
          onChange={(value) => setSelectedProductId(value as number)}
        >
          {products.map((product: Product) => (
            <SelectOption key={product.id} value={product.id}>
              {product.name}
            </SelectOption>
          ))}
        </Select>

        {selectedProductId && (
          <>
            <InputNumber
              label={intl.formatMessage({ id: 'general.price' })}
              name="stock_price"
              rules={[
                { required: true, message: intl.formatMessage({ id: 'general.required' }) },
              ]}
              min={0}
              controls={false}
              addonAfter={getUnitShort(isEditMode ? editItem!.stock_unit : (productAmounts?.storage_unit || 'kg'))}
            />

            <InputNumber
              label={intl.formatMessage({ id: 'general.amount_table' })}
              name="stock_count"
              rules={[
                { required: true, message: intl.formatMessage({ id: 'general.required' }) },
              ]}
              min={0.01}
              step={1}
            />
          </>
        )}
      </Form>
    </StyledAddProductModal>
  );
};
